/**
 * Smart Caching System
 * Only calls APIs when data is actually stale or missing
 * Prevents unnecessary API calls to preserve daily limits
 */

// Data freshness requirements (in milliseconds)
const DATA_FRESHNESS = {
  morpho: 3 * 60 * 1000,      // 3 minutes - utilization changes slowly
  coingecko: 1 * 60 * 1000,   // 1 minute - price can change more frequently
  etherscan: 15 * 60 * 1000,  // 15 minutes - loop exposure is historical
  coinglass: 4 * 60 * 60 * 1000, // 4 hours - funding rates change every 8h
  defillama: 10 * 60 * 1000   // 10 minutes - TVL changes slowly
};

// Track last successful fetch times and data
const cache = {
  morpho: { timestamp: 0, data: null },
  coingecko: { timestamp: 0, data: null },
  etherscan: { timestamp: 0, data: null },
  coinglass: { timestamp: 0, data: null },
  defillama: { timestamp: 0, data: null }
};

/**
 * Check if data is still fresh for an API
 * @param {string} apiName - Name of the API
 * @returns {boolean} True if data is fresh, false if needs refresh
 */
export function isDataFresh(apiName) {
  const freshness = DATA_FRESHNESS[apiName];
  const cached = cache[apiName];
  
  if (!cached || !cached.data) {
    return false; // No data, needs refresh
  }
  
  const age = Date.now() - cached.timestamp;
  return age < freshness;
}

/**
 * Get cached data if fresh, otherwise return null
 * @param {string} apiName - Name of the API
 * @returns {*} Cached data or null if stale/missing
 */
export function getCachedData(apiName) {
  if (isDataFresh(apiName)) {
    console.log(`📦 ${apiName}: Using cached data (${Math.floor((Date.now() - cache[apiName].timestamp) / 1000)}s old)`);
    return cache[apiName].data;
  }
  return null;
}

/**
 * Update cache with new data
 * @param {string} apiName - Name of the API
 * @param {*} data - Data to cache
 */
export function updateCache(apiName, data) {
  cache[apiName] = {
    timestamp: Date.now(),
    data: data
  };
  console.log(`💾 ${apiName}: Data cached at ${new Date().toLocaleTimeString()}`);
}

/**
 * Check if an API should be called based on data freshness
 * @param {string} apiName - Name of the API
 * @returns {boolean} True if API should be called
 */
export function shouldCallAPI(apiName) {
  const isFresh = isDataFresh(apiName);
  const shouldCall = !isFresh;
  
  if (shouldCall) {
    const cached = cache[apiName];
    const age = cached && cached.timestamp > 0 ? Math.floor((Date.now() - cached.timestamp) / 1000) : 'never';
    console.log(`🔄 ${apiName}: Data stale (${age}s old), calling API`);
  } else {
    console.log(`✅ ${apiName}: Data fresh, skipping API call`);
  }
  
  return shouldCall;
}

/**
 * Get cache status for all APIs
 * @returns {Object} Cache status for all APIs
 */
export function getCacheStatus() {
  const status = {};
  
  Object.keys(cache).forEach(apiName => {
    const cached = cache[apiName];
    const age = cached ? Math.floor((Date.now() - cached.timestamp) / 1000) : null;
    const isFresh = isDataFresh(apiName);
    const freshness = DATA_FRESHNESS[apiName];
    
    status[apiName] = {
      hasData: !!cached?.data,
      age: age,
      isFresh: isFresh,
      freshnessThreshold: Math.floor(freshness / 1000),
      shouldCall: shouldCallAPI(apiName)
    };
  });
  
  return status;
}

/**
 * Clear cache for an API (force refresh)
 * @param {string} apiName - Name of the API
 */
export function clearCache(apiName) {
  if (cache[apiName]) {
    cache[apiName] = { timestamp: 0, data: null };
    console.log(`🗑️ ${apiName}: Cache cleared`);
  }
}

/**
 * Clear all caches
 */
export function clearAllCaches() {
  Object.keys(cache).forEach(apiName => {
    cache[apiName] = { timestamp: 0, data: null };
  });
  console.log('🗑️ All caches cleared');
}
